import type {
  BulkStorageContext,
  LogYardContext,
  ProductContext,
  ProductGroupContext,
  ShapeContext,
  StorageAreaContext,
  StorageAreaStatuses,
  StorageAreaTypes,
} from 'shared/mapStyles/domain/shapeContext';
import { language as shapeLanguage } from 'shared/mapStyles/domain/shapeFormulaLanguage';
import {
  isBulkStorage,
  isLogYard,
  StorageAreaType,
} from 'shared/storageAreas/domain/storageAreaType';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea';
import type { Product } from 'shared/products/domain/product';
import { useProductStore } from 'shared/products/stores/productStore';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { getDaysSinceDate } from 'shared/common/date';
import { calculateFillLevelVolume, calculateFillLevelWeight } from '../composables/useCalculateFillLevel';

const storageAreaTypes: StorageAreaTypes = {
  producer: StorageAreaType.Producer,
  consumer: StorageAreaType.Consumer,
  vehicle: StorageAreaType.Vehicle,
  logSorterIntake: StorageAreaType.LogSorterIntake,
  sawLineIntake: StorageAreaType.SawLineIntake,
  logSorterBin: StorageAreaType.LogSorterBin,
  sortedPile: StorageAreaType.SortedPile,
  unsortedPile: StorageAreaType.UnsortedPile,
  truck: StorageAreaType.Truck,
  train: StorageAreaType.Train,
  bulk: StorageAreaType.Bulk,
};

const storageAreaStatuses: StorageAreaStatuses = {
  undefined: StorageAreaStatus.Undefined,
  empty: StorageAreaStatus.Empty,
  inuse: StorageAreaStatus.InUse,
  full: StorageAreaStatus.Full,
};

/**
 * Builds a {@link ShapeContext} for a single storage area, using the same
 * data the map shape layer uses to evaluate map-style formulas. Vehicle- and
 * map-interaction-related fields (vehicle content, selection, inside, allowed
 * to place) are defaulted, because the display text is computed when a storage
 * area is edited, outside of any live map interaction.
 */
export function buildShapeContextForStorageArea(storageArea: StorageArea): ShapeContext {
  const productStore = useProductStore();
  const productGroupStore = useProductGroupStore();

  const logYardData = storageArea.logYardData;
  const bulkStorageData = storageArea.bulkStorageData;

  const languageStore = logYardData || bulkStorageData ? useLanguageStore() : null;

  const productIds = new Set<string>();

  if (logYardData) {
    for (const content of logYardData.content) {
      productIds.add(content.productId);
    }
  }

  if (bulkStorageData) {
    for (const content of bulkStorageData.content) {
      productIds.add(content.productId);
    }
  }

  const products: Product[] = [];
  for (const productId of productIds) {
    const product = productStore.getById(productId);
    if (!product) continue;
    products.push(product);
  }
  products.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const storageAreaContext: StorageAreaContext = {
    id: storageArea.id,
    name: storageArea.name,
    status: storageArea.status,
    type: storageArea.type,
    allowedToPlace: false,
    sameProductsAsVehicle: false,
    sameAllowedProductsAsVehicle: false,
    empty: products.length === 0,
    note: storageArea.note ?? '',
    importantNote: storageArea.importantNote,
    sameProductAsSawLineIntake: false,
    sameProductAsSawLineIntakeFunc: () => false,
    sameProductAsLogSorterBin: false,
    productExistsInOtherStorageAreas: false,
    fillLevel: calculateFillLevelVolume(storageArea),
    fillLevelVolume: calculateFillLevelVolume(storageArea),
    fillLevelWeight: calculateFillLevelWeight(storageArea),
  };

  let productContext: ProductContext | null = null;
  let productGroupContext: ProductGroupContext | null = null;

  const firstProduct = products[0];
  if (firstProduct) {
    const productGroup = productGroupStore.getById(firstProduct.productGroupId);
    const color = firstProduct.color ?? productGroup?.color ?? null;

    productContext = {
      id: firstProduct.id,
      color: color,
      name: products.map((p) => p.name).join(', '),
      multiple: productIds.size > 1,
    };

    if (productGroup) {
      productGroupContext = {
        name: productGroup.name,
        color: productGroup.color,
      };
    }
  }

  let logYardContext: LogYardContext | null = null;
  if (logYardData) {
    const language = languageStore!;
    const allowedProductsString = resolveProductNames(productStore, logYardData.allowedProducts);

    logYardContext = {
      allowMultipleProducts: logYardData.allowMultipleProducts,
      disableAutomaticProductChange: logYardData.disableAutomaticProductChange,
      allowedProducts: allowedProductsString,
      content: products.map((p) => p.name).join(', '),
      firstMoveToDate: language.formatMonthDay(logYardData.firstMoveToUtc),
      lastEmptyDate: language.formatMonthDay(logYardData.lastEmptyUtc),
      lastInventoryDate: language.formatMonthDay(logYardData.lastInventoryUtc),
      firstMoveToDateTime: language.formatDateTime(logYardData.firstMoveToUtc),
      lastEmptyDateTime: language.formatDateTime(logYardData.lastEmptyUtc),
      lastInventoryDateTime: language.formatDateTime(logYardData.lastInventoryUtc),
      daysSinceFirstMove: getDaysSinceDate(logYardData.firstMoveToUtc),
      logSorterBin: null, // default null here since this code doesn't seem to be used anywhere
      sameProductAsLogSorterProductionOrderFunc: () => false,
    };
  }

  let bulkStorageContext: BulkStorageContext | null = null;
  if (bulkStorageData) {
    const language = languageStore!;
    const allowedProductsString = resolveProductNames(
      productStore,
      bulkStorageData.allowedProducts,
    );

    bulkStorageContext = {
      allowMultipleProducts: bulkStorageData.allowMultipleProducts,
      disableAutomaticProductChange: bulkStorageData.disableAutomaticProductChange,
      allowedProducts: allowedProductsString,
      content: products.map((p) => p.name).join(', '),
      firstMoveToDate: language.formatMonthDay(bulkStorageData.firstMoveToUtc),
      lastEmptyDate: language.formatMonthDay(bulkStorageData.lastEmptyUtc),
      lastInventoryDate: language.formatMonthDay(bulkStorageData.lastInventoryUtc),
      firstMoveToDateTime: language.formatDateTime(bulkStorageData.firstMoveToUtc),
      lastEmptyDateTime: language.formatDateTime(bulkStorageData.lastEmptyUtc),
      lastInventoryDateTime: language.formatDateTime(bulkStorageData.lastInventoryUtc),
      daysSinceFirstMove: getDaysSinceDate(bulkStorageData.firstMoveToUtc),
    };
  }

  return {
    vehicle: { empty: true },
    storageArea: storageAreaContext,
    product: productContext,
    productGroup: productGroupContext,
    storageAreaType: storageAreaTypes,
    storageAreaStatus: storageAreaStatuses,
    inside: false,
    selected: false,
    logYard: logYardContext,
    bulk: bulkStorageContext,
    color: null,
  };
}

function resolveProductNames(
  productStore: ReturnType<typeof useProductStore>,
  productIds: string[],
): string | null {
  const products: Product[] = [];
  for (const productId of productIds) {
    const product = productStore.getById(productId);
    if (!product) continue;
    products.push(product);
  }
  if (products.length === 0) return null;
  products.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  return products.map((p) => p.name).join(', ');
}

/**
 * Computes the display text for a storage area by evaluating the given formula
 * against the storage area's shape context, using the same formula language as
 * the map style shape formulas. Returns an empty string when the formula is
 * empty or fails to evaluate.
 */
export function computeStorageAreaDisplayText(formula: string, storageArea: StorageArea): string {
  if (!formula) return '';

  const context = buildShapeContextForStorageArea(storageArea);
  const result = shapeLanguage.formulaParser.evaluate(formula, context);

  if (!result.compilation.success) {
    return '';
  }

  return result.text;
}
